/**
 * Componente PontoDesviosAlert
 * 
 * Wrapper para o componente de alertas de desvios de ponto.
 * Gerencia estados de loading, error e empty de forma consistente.
 * 
 * @module components/Dashboard/AlertsSection/PontoDesviosAlert
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaClock } from 'react-icons/fa';
import LoadingIndicator from '../../Common/LoadingIndicator';
import Alert from '../../UI/Alert';
import PontoDesviosTable from '../../Charts/PontoDesviosTable';

/**
 * Componente de alerta de desvios de ponto
 * 
 * Renderiza uma seção de alertas de desvios de ponto com título,
 * gerenciamento de estados e tabela de dados.
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.desvios - Dados dos desvios de ponto
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Error|string|null} props.error - Erro ocorrido
 * @param {string} [props.title] - Título personalizado
 * @param {string} [props.tolerancia] - Tolerância de atraso (ex: "15 minutos")
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes
 * 
 * @returns {JSX.Element} Componente de alerta
 * 
 * @example
 * ```jsx
 * <PontoDesviosAlert
 *     desvios={pontoDesviosData}
 *     isLoading={isLoading}
 *     error={error}
 *     tolerancia="15 minutos"
 * />
 * ```
 */
const PontoDesviosAlert = ({
    desvios = null,
    isLoading,
    error = null,
    title = null,
    tolerancia = '15 minutos',
    className = '',
    testId = null,
}) => {
    const defaultTitle = `📢 Desvios de Ponto Recentes (Atrasos maiores ${tolerancia})`;
    const displayTitle = title || defaultTitle;

    return (
        <article 
            className={className} 
            data-testid={testId || 'ponto-desvios-alert'}
            role="article"
            aria-labelledby={`${testId || 'ponto-desvios-alert'}-title`}
        >
            <h3 
                id={`${testId || 'ponto-desvios-alert'}-title`}
                className="text-lg font-semibold mb-4"
            >
                {displayTitle}
            </h3>
            <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:-translate-y-1 p-4 relative z-10 hover:z-20" role="region" aria-label="Tabela de desvios de ponto">
                {isLoading ? (
                    <LoadingIndicator 
                        size="small" 
                        message="Buscando desvios de ponto..." 
                    />
                ) : error ? (
                    <Alert 
                        type="error" 
                        message={
                            error?.message 
                                ? `Erro ao buscar desvios: ${error.message}` 
                                : `Erro ao buscar desvios: ${error || 'Erro desconhecido'}`
                        } 
                    />
                ) : (
                    <PontoDesviosTable desvios={desvios} />
                )}
            </div>
        </article>
    );
};

PontoDesviosAlert.propTypes = {
    /** Dados dos desvios de ponto */
    desvios: PropTypes.array,
    
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Erro ocorrido (null se não houver) */
    error: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Título personalizado */
    title: PropTypes.string,
    
    /** Tolerância de atraso */
    tolerancia: PropTypes.string,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** ID para testes */
    testId: PropTypes.string,
};

// Default props removidos - usando default parameters na função

export default PontoDesviosAlert;

